import { useCreateResource__ALPHA } from '@cube3/state/src/redux/components/Hooks/useCreateResource';
import { statuses } from '@cube3/state/src/redux/ducks/request-status';
import { useCallback, useState } from 'react';
import Client from '@cube3/state/src/wrapped-cube-client';
import { reloadImg } from '@cube3/common/utils/reloadImage';

export const useEditVideoThumbnail = (assetId: string) => {
  const [captureAt, setCapturedAt] = useState<number>(undefined);

  const [createResource, createResourceStatus] = useCreateResource__ALPHA({
    ancestor: { type: 'asset', id: assetId },
    resourceType: 'thumbnail',
    cacheInvalidator: null
  });

  /** Polling for the new thumbnail and force browser to reload the new image */
  const onReloadThumbnail = useCallback(() => {
    let imageSize: number;
    let timer;
    let counter = 1;
    const fetchNewThumbnail = () => {
      if (timer) {
        clearTimeout(timer);
      }
      timer = setTimeout(() => {
        Client.auth
          .request({
            method: 'GET',
            url: `/files/thumbnail/${assetId}`,
            cache: 'reload'
          })
          .then(async (res) => {
            const blob = new Blob([res.data], { type: 'image/jpeg' });
            const size = blob.size;
            // if the current frame is the same as the current thumbnail, this will poll forever,
            // check counter here to prevent this from happening
            if ((imageSize && imageSize !== size) || counter > 10) {
              const src = `${res.config.baseURL}/files/thumbnail/${assetId}`;
              // if the detail view modal is still open, reload the new thumbnail by reloading it in a iframe,
              await reloadImg(src);
              // otherwise replace the image source manually
              document.body
                .querySelectorAll(`img[src='${src}']`)
                .forEach((img) => (img['src'] = src));
            } else if (createResourceStatus != statuses.FAILED) {
              fetchNewThumbnail();
              counter++;
            }
            imageSize = size;
          })
          .catch((err) =>
            console.error('Failed to reload thumbnail of ', assetId, err)
          );
      }, 3000);
    };
    fetchNewThumbnail();
  }, [assetId, createResourceStatus]);

  const setThumbnail = useCallback(
    (playedSeconds: number) => {
      if (assetId && playedSeconds !== undefined) {
        createResource({
          type: 'thumbnail',
          timestamp: playedSeconds,
          relationships: {
            asset: {
              data: {
                type: 'asset',
                id: assetId
              }
            }
          }
        });
        onReloadThumbnail();
        setCapturedAt(playedSeconds);
      }
    },
    [assetId, createResource, onReloadThumbnail]
  );

  return {
    setThumbnail,
    saveThumbnailStatus:
      createResourceStatus === statuses.FAILED ? captureAt : undefined
  };
};
